import * as Sentry from '@sentry/nextjs';
import { makeRetriable } from 'p-retry';
import { logIfUnsafeFetchTags } from './log-if-unsafe-fetch-tags';

const RETRYABLE_CODES: number[] = [
	408, // Request Timeout: The server timed out waiting for the request.
	425, // Too Early: The server is unwilling to process a request because it might be replayed.
	429, // Too Many Requests: The user has sent too many requests in a given amount of time (rate limiting).
	500, // Internal Server Error: A generic error message, given when an unexpected condition was encountered and no more specific message is suitable. Depending on the cause, this can be temporary.
	// TODO: re-enable for 502s when the Go API issues are resolved - right now retries don't work on that particular problem
	// 502, // Bad Gateway: The server, while acting as a gateway or proxy, received an invalid response from an upstream server.
	503, // Service Unavailable: The server is currently unable to handle the request due to a temporary overload or scheduled maintenance, which will likely be alleviated after some delay.
	504, // Gateway Timeout: The server, while acting as a gateway or proxy, did not receive a timely response from an upstream server.
];

type OptionsWithRetryFlag = RequestInit & {
	/** @remarks throwing this in here incase we have future cases where we are not doing something idempotent with fetch */
	DO_NOT_RETRY?: boolean;
};

// intended to be a resilient Next.js fetch wrapper
const _fetchWithErrorLogging = async (
	url: RequestInfo | URL,
	options: OptionsWithRetryFlag = {},
): Promise<Response> => {
	logIfUnsafeFetchTags(options?.next?.tags);

	const response = await fetch(url, options);
	if (response.ok) return response;

	// so things didn't go well above.
	// let's clone the text so the caller can still consume the body 🧟
	const text = await response.clone().text();
	const errorMessage = `Fetching failed for: ${response.url} with ${response.status} ${response.statusText}`;
	console.error(errorMessage, text);
	Sentry.captureException(new Error(errorMessage), { data: text });

	const canRetry =
		!options.DO_NOT_RETRY && RETRYABLE_CODES.includes(response.status);
	// if it's retryable, we throw an error to keep this function from returning a resolved promise. that is what triggers the retries.
	if (canRetry) {
		throw new Error(errorMessage);
	}

	// returning a resolved promise will not cause retries
	return response;
};

const fetchWithErrorLogging = makeRetriable(_fetchWithErrorLogging, {
	retries: 2,
});

export default fetchWithErrorLogging;
